import {
  type Audio, type EditableUnit, type Piece,
} from '@/data';
import type { ImageLikeSource } from '@/data/Source';
import type { PieceFormat } from '@/formats/PieceParser';
import PieceParser from '@/formats/PieceParser';
import UnitParser from '@/formats/UnitParser';
import type { Response } from './ClientResponse';
import { ClientResponse } from './ClientResponse';
import type { IClient } from './IClient';
import type { PieceSources } from './sources';
import { loadPieceSources, loadPieceSourcesNewFormat } from './sources';

export async function loadPiece(
  this: IClient,
  path: string,
  loadSources = true,
): Promise<Response<Piece>> {
  try {
    let pieceUrl;
    if (this.isLocalClient) {
      pieceUrl = this.buildUrl(path, '/info.json').data;
    } else {
      pieceUrl = `${this.baseUrl}corpus/${path}?static=true`;
    }
    const format = (await this.get(pieceUrl)).data as PieceFormat;
    const isNewFormat = !('images' in format.sources || 'audios' in format.sources);

    if (format.opus && format.opus['measure-map']) {
      try {
        if (typeof format.opus['measure-map'] === 'string') {
          if (format.opus['measure-map'].startsWith('/local-corpus')) {
            format.opus['measure-map'] = (await this.get(this.buildUrl(format.opus['measure-map'].replace('/local-corpus', '')).data)).data as string;
          } else if (format.opus['measure-map'].startsWith('http')) {
            format.opus['measure-map'] = (await this.get(this.sourceUrl(format.opus['measure-map']).data)).data as string;
          } else {
            format.opus['measure-map'] = (await this.get(`${this.baseUrl}${this.staticRoute}/${path}/${format.opus['measure-map']}`)).data as string;
          }
        }
      } catch (error) {
        format.opus['measure-map'] = '';
      }
    }

    let sources: ImageLikeSource[];
    let tracks: Audio[];

    if (loadSources) {
      let sourceAndTracksAndTabs :PieceSources;
      if (isNewFormat) {
        sourceAndTracksAndTabs = await loadPieceSourcesNewFormat.bind(this)(format, path);
      } else {
        sourceAndTracksAndTabs = await loadPieceSources.bind(this)(format, path);
      }

      sources = sourceAndTracksAndTabs.sources;
      tracks = sourceAndTracksAndTabs.tracks;
    } else {
      sources = [];
      tracks = [];
    }

    return this
      .tryParse(
        () => PieceParser.fromFormat(path, this.isLocalClient, format, sources, tracks),
      );
  } catch (e) {
    if (ClientResponse.isClientError(e)) return e.cast();
    return ClientResponse.fromParsingError(e as Error);
  }
}

export async function uploadSync(
  this: IClient,
  path: string,
  synchro: EditableUnit,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Response<any>> {
  try {
    const url = `${this.baseUrl}corpus/${path}/synchro`;
    const obj = UnitParser.exportAudioFormat(synchro);
    const txt = JSON.stringify(obj, null, 2);
    const blob = new Blob([txt], { type: 'application/json' });
    const data = new FormData();
    data.append('synchro', blob);
    const response = (await this.put(url, data)).data;
    return this.tryParse(() => response);
  } catch (e) {
    if (ClientResponse.isClientError(e)) return e.cast();
    return ClientResponse.fromParsingError(e as Error);
  }
}
